import type {
  Equipment,
  OperatingParameters,
  WellProperties
} from "@/composables/forms/formTypes";
import {
  getFieldName,
  getMotorFieldName,
  getPumpFieldName,
  type GasSeparationFieldKey,
  type MotorFieldKey,
  type PumpFieldKey
} from "@/composables/forms/stackAwareFields";
import type { ComputedRef, Ref } from "vue";
import { computed } from "vue";
import { injectStrict } from "@/utils/utils";
import { equipmentKey } from "./scenarioDetailProviderKeys";
import type { Y_Tools } from "gql/api";

export type Stack = "upper" | "lower";

/**
 * Filters an equipment map based on order array from equipment object
 */
export function filterEquipmentMapByOrder<T extends { id?: string | null }>(
  equipmentMap: Record<string, T>,
  orderArray: string[] | null | undefined
): Record<string, T> {
  if (!orderArray || !Array.isArray(orderArray)) {
    return {};
  }

  const filtered: Record<string, T> = {};
  for (const id of orderArray) {
    if (equipmentMap[id]) {
      filtered[id] = equipmentMap[id];
    }
  }
  return filtered;
}

/**
 * Filters housings to only include those linked to pumps in the given order
 * This ensures housings are correctly linked to lower stack pumps via scenario_equipment_xref_pump_id
 * @param pumpsOrder - Array of pump IDs in order
 * @param pumpsMap - Map of pumps, where each pump may have r_xref_housings relationship
 * @param housingsMap - Map of all housings
 * @returns Filtered map of housings linked to the specified pumps
 */
export function filterHousingsByPumpOrder<
  THousing extends {
    id?: string | null;
    journal_deleted_at?: unknown;
    scenario_equipment_xref_pump_id?: string | null;
  },
  TPump extends {
    r_xref_housings?: Array<{ id?: string | null }> | null;
  }
>(
  pumpsOrder: string[],
  pumpsMap: Record<string, TPump>,
  housingsMap: Record<string, THousing>
): Record<string, THousing> {
  const filteredHousings: Record<string, THousing> = {};
  const pumpIds = new Set(pumpsOrder);

  // First, collect housings from pump relationships (GraphQL joins)
  for (const pumpId of pumpsOrder) {
    const pump = pumpsMap[pumpId];
    if (pump?.r_xref_housings) {
      for (const housing of pump.r_xref_housings) {
        if (housing.id && housingsMap[housing.id]) {
          filteredHousings[housing.id] = housingsMap[housing.id];
        }
      }
    }
  }

  // Also check housingsMap directly to ensure we catch all housings
  // that are linked to lower stack pumps via scenario_equipment_xref_pump_id
  for (const [housingId, housing] of Object.entries(housingsMap)) {
    if (
      housing &&
      !housing.journal_deleted_at &&
      housing.scenario_equipment_xref_pump_id &&
      pumpIds.has(housing.scenario_equipment_xref_pump_id)
    ) {
      // Only add if not already added from relationship (to avoid duplicates)
      if (!filteredHousings[housingId]) {
        filteredHousings[housingId] = housing;
      }
    }
  }

  return filteredHousings;
}

/**
 * Creates a filtered equipment object for a specific stack
 * For upper stack, uses standard fields (motor_id, mle_id, etc.)
 * For lower stack, uses lower_* fields (lower_motor_id, lower_mle_id, etc.)
 */
export function createFilteredEquipmentObject(
  equipment: Equipment | null | undefined,
  stack: Stack
): Equipment | null | undefined {
  if (!equipment) return equipment;

  if (stack === "upper") {
    // Upper stack uses existing fields - no filtering needed
    return equipment;
  }

  // Normalize lower stack fields to standard field names so calculations can be stack-agnostic.
  // The database uses lower_* prefixed fields, but calculations expect standard names.
  // (Translation layer for calculations)
  const lowerEquipment: Equipment = {
    ...equipment,
    pumps_order: equipment.lower_pumps_order || null,
    intakes_order: equipment.lower_intakes_order || null,
    seals_order: equipment.lower_seals_order || null,
    cables_order: equipment.lower_cables_order || null,
    motor_id: equipment.lower_motor_id || null,
    mle_id: equipment.lower_mle_id || null,
    sensor_id: equipment.lower_sensor_id || null,
    seal_motor_oil_id: equipment.lower_seal_motor_oil_id || null,
    r_motor: equipment.r_lower_motor || null,
    r_mle: equipment.r_lower_mle || null,
    r_sensor: equipment.r_lower_sensor || null,
    r_seal_motor_oil: equipment.r_lower_seal_motor_oil || null
  };

  return lowerEquipment;
}

/**
 * Creates a stack-aware parameters object
 * Maps stack-specific field names (e.g., lower_pump_hz vs pump_hz)
 * Uses Record<keyof OperatingParameters, unknown> to maintain type safety
 * while allowing dynamic field access based on computed property names
 */
export function createStackAwareParameters(
  parameters: OperatingParameters | null | undefined,
  stack: Stack
): OperatingParameters | null | undefined {
  if (!parameters) return parameters;

  if (stack === "upper") {
    // Upper stack uses existing fields - no mapping needed
    return parameters;
  }

  // Lower stack: create mapped parameters object
  // Use Record type to allow dynamic field assignment while maintaining type safety
  const lowerParameters = { ...parameters } as OperatingParameters &
    Record<keyof OperatingParameters, unknown>;

  // Map gas separation fields
  const gasSepFields: GasSeparationFieldKey[] = [
    "packerInstalled",
    "naturalGasSepOverrideType",
    "naturalGasSepPercent",
    "intakeGasSepOverrideType",
    "intakeGasSepPercent"
  ];

  for (const field of gasSepFields) {
    const upperFieldName = getFieldName(
      field,
      "upper"
    ) as keyof OperatingParameters;
    const lowerFieldName = getFieldName(
      field,
      "lower"
    ) as keyof OperatingParameters;
    // Type assertion is safe here because both field names exist on OperatingParameters
    // and we're mapping between compatible fields
    const lowerValue = parameters[
      lowerFieldName
    ] as OperatingParameters[typeof upperFieldName];
    lowerParameters[upperFieldName] = lowerValue;
  }

  // Map motor fields
  const motorFields: MotorFieldKey[] = ["motorRerateRatio"];

  for (const field of motorFields) {
    const upperFieldName = getMotorFieldName(
      field,
      "upper"
    ) as keyof OperatingParameters;
    const lowerFieldName = getMotorFieldName(
      field,
      "lower"
    ) as keyof OperatingParameters;
    // Type assertion is safe here because both field names exist on OperatingParameters
    // and we're mapping between compatible fields
    const lowerValue = parameters[
      lowerFieldName
    ] as OperatingParameters[typeof upperFieldName];
    lowerParameters[upperFieldName] = lowerValue;
  }

  // Map pump fields
  const pumpFields: PumpFieldKey[] = [
    "pumpHz",
    "hasCompressionPump",
    "includeCasingFrictionLoss",
    "pumpOverrideGasCorrectionPercent",
    "pumpWearHeadCorrectionRatio",
    "pumpWearBhpCorrectionRatio",
    "useLegacyGasHeadRerate"
  ];

  for (const field of pumpFields) {
    const upperFieldName = getPumpFieldName(
      field,
      "upper"
    ) as keyof OperatingParameters;
    const lowerFieldName = getPumpFieldName(
      field,
      "lower"
    ) as keyof OperatingParameters;
    // Type assertion is safe here because both field names exist on OperatingParameters
    // and we're mapping between compatible fields
    const lowerValue = parameters[
      lowerFieldName
    ] as OperatingParameters[typeof upperFieldName];
    lowerParameters[upperFieldName] = lowerValue;
  }

  return lowerParameters as OperatingParameters;
}

/**
 * Creates stack-aware wellProperties object
 * Currently no stack-specific fields, but structure allows future expansion
 */
export function createStackAwareWellProperties(
  wellProperties: WellProperties | null | undefined,
  _stack: Stack // eslint-disable-line @typescript-eslint/no-unused-vars
): WellProperties | null | undefined {
  // No stack-specific fields currently, return as-is
  return wellProperties;
}

/**
 * Type definition for y-tool properties return value
 */
export type YToolProperties = {
  useYTool: boolean;
  yTool?: Y_Tools | null;
  yToolBypassTubings: Array<{
    id?: string | null;
    length_feet?: number | null;
    r_tubing?: { outer_diameter_inches?: number | null } | null;
    journal_deleted_at?: unknown;
  }>;
  yToolEquipmentTubings: Array<{
    id?: string | null;
    length_feet?: number | null;
    r_tubing?: { interior_diameter_inches?: number | null } | null;
    journal_deleted_at?: unknown;
  }>;
  yToolBypassTubingOrder: string[] | null;
  yToolEquipmentTubingOrder: string[] | null;
};

/**
 * Default empty y-tool properties
 */
function defaultYToolProperties(): YToolProperties {
  return {
    useYTool: false,
    yTool: null,
    yToolBypassTubings: [],
    yToolEquipmentTubings: [],
    yToolBypassTubingOrder: null,
    yToolEquipmentTubingOrder: null
  };
}

/**
 * Extracts uphole y-tool properties from equipment
 * @param equipment - Must be non-null (checked by caller)
 */
function extractUpholeYToolProperties(
  equipment: Equipment | null | undefined
): YToolProperties {
  if (!equipment) {
    return defaultYToolProperties();
  }
  return {
    useYTool: true,
    yTool: equipment.r_uphole_y_tool as Y_Tools | null,
    yToolBypassTubings: (equipment.r_xref_uphole_y_tool_bypass_tubings ||
      []) as Array<{
      id?: string | null;
      length_feet?: number | null;
      r_tubing?: { outer_diameter_inches?: number | null } | null;
      journal_deleted_at?: unknown;
    }>,
    yToolEquipmentTubings: (equipment.r_xref_uphole_y_tool_equipment_tubings ||
      []) as Array<{
      id?: string | null;
      length_feet?: number | null;
      r_tubing?: { interior_diameter_inches?: number | null } | null;
      journal_deleted_at?: unknown;
    }>,
    yToolBypassTubingOrder: equipment.uphole_y_tool_bypass_tubing_order || null,
    yToolEquipmentTubingOrder:
      equipment.uphole_y_tool_equipment_tubing_order || null
  };
}

/**
 * Extracts downhole y-tool properties from equipment
 * @param equipment - Must be non-null (checked by caller)
 */
function extractDownholeYToolProperties(
  equipment: Equipment | null | undefined
): YToolProperties {
  if (!equipment) {
    return defaultYToolProperties();
  }
  return {
    useYTool: true,
    yTool: equipment.r_downhole_y_tool as Y_Tools | null,
    yToolBypassTubings: (equipment.r_xref_downhole_y_tool_bypass_tubings ||
      []) as Array<{
      id?: string | null;
      length_feet?: number | null;
      r_tubing?: { outer_diameter_inches?: number | null } | null;
      journal_deleted_at?: unknown;
    }>,
    yToolEquipmentTubings:
      (equipment.r_xref_downhole_y_tool_equipment_tubings || []) as Array<{
        id?: string | null;
        length_feet?: number | null;
        r_tubing?: { interior_diameter_inches?: number | null } | null;
        journal_deleted_at?: unknown;
      }>,
    yToolBypassTubingOrder:
      equipment.downhole_y_tool_bypass_tubing_order || null,
    yToolEquipmentTubingOrder:
      equipment.downhole_y_tool_equipment_tubing_order || null
  };
}

/**
 * Extracts y-tool properties from equipment in a stack-aware manner
 * @param equipment - The equipment object to extract y-tool properties from
 * @param stack - Optional stack parameter. When "lower", explicitly uses downhole y-tool.
 *                When "upper" or undefined, uses uphole y-tool (or falls back to downhole if no uphole).
 * @returns Generic y-tool properties object
 */
export function getYToolProperties(
  equipment: Equipment | null | undefined,
  stack?: Stack
): YToolProperties {
  if (!equipment) {
    return defaultYToolProperties();
  }

  // For lower stack, explicitly use downhole y-tool
  if (stack === "lower") {
    if (equipment.use_downhole_y_tool) {
      return extractDownholeYToolProperties(equipment);
    }
    return defaultYToolProperties();
  }

  // For upper stack or default, use uphole y-tool
  if (equipment.use_uphole_y_tool) {
    return extractUpholeYToolProperties(equipment);
  }

  // Fallback to downhole if no uphole (for backward compatibility)
  if (equipment.use_downhole_y_tool) {
    return extractDownholeYToolProperties(equipment);
  }

  // No y-tool configured
  return defaultYToolProperties();
}

/**
 * Composable to check if dual stacks are configured
 * Returns true when all conditions are met:
 * - has_lower_equip_stack is true
 * - use_uphole_y_tool is true
 * - r_uphole_y_tool is set (truthy)
 * - use_downhole_y_tool is true
 * - r_downhole_y_tool is set (truthy)
 * @param equipment - Optional equipment ref. If not provided, will inject equipmentKey
 * @returns ComputedRef<boolean> indicating if dual stacks are enabled
 */
export function useHasDualStacks(
  equipment?: Ref<Equipment | null | undefined>
): ComputedRef<boolean> {
  const equipmentRef = equipment ?? injectStrict(equipmentKey);

  return computed(() => {
    const eq = equipmentRef.value;
    if (!eq) return false;

    return (
      (eq.has_lower_equip_stack ?? false) &&
      (eq.use_uphole_y_tool ?? false) &&
      !!eq.r_uphole_y_tool &&
      (eq.use_downhole_y_tool ?? false) &&
      !!eq.r_downhole_y_tool
    );
  });
}
